import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { BRANCHES, CATEGORIES, branchIdBySlug } from './speakupConstants';
import SpeakUpShell from './SpeakUpShell';
import './speakup.css';

const STEPS = { LANDING: 'landing', FORM: 'form', CONFIRM: 'confirm' };

// Gate decision D1: the lane is chosen at the front door, and it -- not a
// category dropdown somebody might get wrong -- decides who reads it, whether
// it can ever be published, and whether a clock runs.
const LANES = { IDEA: 'idea', CONCERN: 'concern' };

export default function SpeakUpPublic() {
  const [searchParams] = useSearchParams();
  const qrBranchId = branchIdBySlug(searchParams.get('branch'));

  const [step, setStep] = useState(STEPS.LANDING);
  const [lane, setLane] = useState(LANES.CONCERN);
  const [branchId, setBranchId] = useState(qrBranchId || '');
  const [category, setCategory] = useState('general');
  const [body, setBody] = useState('');
  const [wantsFollowup, setWantsFollowup] = useState(true);
  const [involvesChain, setInvolvesChain] = useState(false);
  const [toAlternate, setToAlternate] = useState(false);
  const [alternateAvailable, setAlternateAvailable] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const branchLocked = !!qrBranchId;
  const selectedBranch = BRANCHES.find((b) => b.id === Number(branchId));
  const isConcern = lane === LANES.CONCERN;

  // The alternate line only exists if somebody can actually receive it. The
  // gate is blunt that a checkbox promising a destination the system cannot
  // reach is worse than not offering one, so the form asks rather than
  // assumes. Returns a boolean and nothing else -- no name, no count, no id.
  useEffect(() => {
    let alive = true;
    supabase.rpc('speakup_alternate_available').then(({ data, error: rpcError }) => {
      if (alive && !rpcError) setAlternateAvailable(data === true);
    });
    return () => { alive = false; };
  }, []);

  function openLane(which) {
    setLane(which);
    setError('');
    setInvolvesChain(false);
    setToAlternate(false);
    setStep(STEPS.FORM);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!branchId) { setError('Please select a branch.'); return; }
    if (body.trim().length < 5) { setError('Please add a little more detail.'); return; }

    setSubmitting(true);
    const { data, error: rpcError } = await supabase.rpc('submit_suggestion', {
      p_branch_id: Number(branchId),
      p_category: category,
      p_body: body.trim(),
      p_wants_followup: wantsFollowup,
      p_lane: lane,
      p_involves_chain: isConcern ? involvesChain : false,
      p_to_alternate: isConcern ? toAlternate : false,
    });
    setSubmitting(false);

    if (rpcError) {
      setError('Something went wrong submitting this. Please try again.');
      return;
    }
    const row = Array.isArray(data) ? data[0] : data;
    setResult(row);
    setStep(STEPS.CONFIRM);
  }

  function copyLink() {
    const url = `${window.location.origin}/speak-up/t/${result.thread_token}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <SpeakUpShell>
        {step === STEPS.LANDING && (
          <div className="speakup-landing">
            <h1 className="speakup-h1">How can we help?</h1>
            <p className="speakup-lede">Nothing here records who you are.</p>

            {/* D1 / callout A1: the two doors say who reads it, ON the door.
                That sentence is the whole consent mechanism -- it is what a
                permission table cannot do. */}
            <button
              className="speakup-door speakup-door-idea"
              onClick={() => openLane(LANES.IDEA)}
              type="button"
            >
              <div className="speakup-door-t">Share an idea</div>
              <div className="speakup-door-d">
                Something that would make the work better. Your branch manager will read it.
              </div>
            </button>

            <button
              className="speakup-door speakup-door-concern"
              onClick={() => openLane(LANES.CONCERN)}
              type="button"
            >
              <div className="speakup-door-t">Raise a concern</div>
              <div className="speakup-door-d">
                Safety, conduct, or something that worries you. Goes straight to ownership — read
                personally, and by nobody else.
              </div>
            </button>

            {/* Callout A2: the board is the third thing somebody wants, so it
                drops to a text link rather than competing as a full door. */}
            <Link
              to={selectedBranch ? `/speak-up/board?branch=${selectedBranch.slug}` : '/speak-up/board'}
              className="speakup-textlink"
            >
              See what we've heard, and what we did
            </Link>

            {/* Callout A3: the branch is stated WITH where it came from. */}
            <div className="speakup-branch-chip">
              {selectedBranch ? <strong>{selectedBranch.name}</strong> : <strong>Branch not set</strong>}
              {branchLocked ? ' · set by the code you scanned' : ''}
            </div>
          </div>
        )}

        {step === STEPS.FORM && (
          <form onSubmit={handleSubmit} className={isConcern ? 'speakup-lane-concern' : 'speakup-lane-idea'}>
            <button className="speakup-back" type="button" onClick={() => setStep(STEPS.LANDING)}>
              ← Back
            </button>

            <h1 className="speakup-h1">{isConcern ? 'Raise a concern' : 'Share an idea'}</h1>

            {isConcern ? (
              <>
                {/* Callout B2: the reassurance is WHO reads it, and it is at
                    the top. The anonymity note sits below it in grey, because
                    a person weighing whether to speak wants to know who is
                    listening before they want to know what is stored. */}
                <div className="speakup-banner speakup-banner-strong">
                  Ownership reads this personally. Nobody else does.
                </div>
                <p className="speakup-note" style={{ marginTop: 8, marginBottom: 14 }}>
                  We do not record your name, your email, or anything that identifies you. Only your
                  branch is attached.
                </p>
              </>
            ) : (
              /* Callout C1: this banner is the price of D4 and it is worth
                 paying. A distinctive idea identifies its author to their own
                 manager. The fix is not a middleman -- it is telling the truth
                 on the form. C2: it offers the other lane inline, because a
                 warning without an alternative is just a warning. */
              <div className="speakup-banner">
                Your {selectedBranch ? `${selectedBranch.name} ` : ''}branch manager will read this, along
                with the Speak Up handler. Your name is not attached — but write it knowing they will see
                it. If you would rather only the handler saw it,{' '}
                <button type="button" className="speakup-inlinelink" onClick={() => openLane(LANES.CONCERN)}>
                  raise it as a concern instead
                </button>
                .
              </div>
            )}

            <label className="speakup-label">Branch</label>
            {branchLocked ? (
              <div className="speakup-field">{selectedBranch?.name} · set by the code you scanned</div>
            ) : (
              <select className="speakup-input" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">Select your branch…</option>
                {BRANCHES.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}

            <label className="speakup-label">What is this about</label>
            <select className="speakup-input" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>

            <label className="speakup-label">{isConcern ? 'Tell us what happened' : 'Your idea'}</label>
            <textarea
              className="speakup-input speakup-textarea"
              /* Callout B3: the old placeholder was "What would make things
                 better?" -- a suggestion-box question. Somebody reporting
                 harassment should not be met with it. */
              placeholder={isConcern ? 'Say as much or as little as you want…' : 'What would make things better?'}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={4000}
            />

            {/* Callout B4: the follow-up box is checked by default -- a way
                back in is a courtesy they will not think to want until later. */}
            <label className="speakup-check">
              <input
                type="checkbox"
                checked={wantsFollowup}
                onChange={(e) => setWantsFollowup(e.target.checked)}
              />
              <span>
                <strong>Let me check back for a reply</strong>
                <div className="speakup-check-sub">We give you a private link to keep. No email needed.</div>
              </span>
            </label>

            {/* D15 / callout B1: a checkbox replaces a name field. It collects
                the one piece of conflict knowledge only the reporter has,
                stores no accusation, names nobody, and is enforceable rather
                than advisory. Unchecked by default -- a guard the reporter did
                not ask for is a guard we chose for them. */}
            {isConcern && (
              <label className="speakup-check">
                <input
                  type="checkbox"
                  checked={involvesChain}
                  onChange={(e) => setInvolvesChain(e.target.checked)}
                />
                <span>
                  <strong>This involves someone in my management chain</strong>
                  <div className="speakup-check-sub">
                    We will not share it with any manager at your branch. You do not need to say who.
                  </div>
                </span>
              </label>
            )}

            {error && <div className="speakup-error">{error}</div>}

            <button className="speakup-btn" type="submit" disabled={submitting}>
              {submitting ? 'Sending…' : 'Send'}
            </button>

            {/* D18a / D18b / callout B5: below the Send button, in plain type,
                with no coloured box. Above the button it would read as a step
                in the form and get considered by everybody; behind a link it
                would be unfindable by the one person who needs it.
                No name, on Colby's ruling at OOB-3 -- naming a person is what
                makes this read as a personal end-run rather than a routing
                choice, and it is also the thing that would go stale the moment
                the flag moves. It renders ONLY when somebody can receive it. */}
            {isConcern && alternateAvailable && (
              <label className="speakup-alt-line">
                <input
                  type="checkbox"
                  checked={toAlternate}
                  onChange={(e) => setToAlternate(e.target.checked)}
                />
                <span>
                  I'd rather this went to the other owner instead
                  <div className="speakup-check-sub">You don't have to say why.</div>
                </span>
              </label>
            )}
          </form>
        )}

        {step === STEPS.CONFIRM && result && (
          <div className="speakup-confirm">
            <div className="speakup-check-icon">✓</div>
            <h1 className="speakup-h1" style={{ textAlign: 'center' }}>Received</h1>
            <p className="speakup-lede" style={{ textAlign: 'center' }}>
              Nothing about who you are was recorded.
            </p>
            <div className="speakup-status-chip">Status: Received</div>

            {result.thread_token && (
              <div className="speakup-token-box">
                <div className="speakup-label" style={{ marginTop: 0 }}>Your private link</div>
                <div className="speakup-token">
                  {window.location.origin}/speak-up/t/{result.thread_token}
                </div>
                <p className="speakup-note">
                  Bookmark this to check for a reply. It's the only way back in — we didn't take your email.
                </p>
                <button className="speakup-btn-sm" onClick={copyLink} type="button" style={{ marginTop: 10 }}>
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            )}
          </div>
        )}
    </SpeakUpShell>
  );
}
